import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Divider,
    Alert,
    Box,
    Typography,
    Tabs,
    Tab,
    CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import ChatIcon from '@mui/icons-material/Chat';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import api from '../../../services/api/api';
import aiService from '../../../services/api/ai.service';
import { QUESTION_INPUT_TYPE } from '../../../types/question.type';
import { useSelector } from 'react-redux';
import useAccountId from '../../../hooks/useAccountId';

// Import tab components
import SettingsTab from './SettingsTab';
import ConfigurationTab from './ConfigurationTab';
import AiChatTab from './AiChatTab';
import AiTestPromptTab from './AiTestPromptTab';
import AIIcon from '../../../assets/svgs/AI-Icon.svg';

const AiConfigDialog = ({
    open,
    onClose,
    question,
    onSave,
    facilityId
}) => {
    const [aiSettings, setAiSettings] = useState({
        aiEnabled: false,
        aiDescription: '',
        aiUnderstanding: '',
        facilityIds: []
    });
    const [loading, setLoading] = useState(false);
    const [loadingSettings, setLoadingSettings] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [validationError, setValidationError] = useState(null);
    const [verifying, setVerifying] = useState(false);
    const [aiUnderstanding, setAiUnderstanding] = useState(null);
    const [searchText, setSearchText] = useState('');

    // New state for enhanced features
    const [activeTab, setActiveTab] = useState(0);
    const [chatMessage, setChatMessage] = useState('');
    const [chatLoading, setChatLoading] = useState(false);
    const [conversationHistory, setConversationHistory] = useState([]);
    const [chatContainerRef, setChatContainerRef] = useState(null);
    const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);
    const [showReviewButton, setShowReviewButton] = useState(false);
    
    // AI Test Prompt state
    const [testPrompt, setTestPrompt] = useState('');
    const [testPromptLoading, setTestPromptLoading] = useState(false);
    const [testPromptResult, setTestPromptResult] = useState(null);
    
    // AI Test Prompt Tab state
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [testNotes, setTestNotes] = useState('');
    const [testPromptTabLoading, setTestPromptTabLoading] = useState(false);
    const [testPromptTabResult, setTestPromptTabResult] = useState(null);

    // Get facilities from Redux store
    const facilities = useSelector((state) => state.facility);
    const isFacilityLoading = useSelector((state) => state.common.isFacilityLoading);
    const { accountId: currentAccountId } = useAccountId();

    // Load AI settings when dialog opens
    useEffect(() => {
        const loadAiSettings = async () => {
            if (!open || !question || !currentAccountId) return;

            setLoadingSettings(true);
            try {
                const response = await api.get(`/api/ai-autofill/ai-settings/${question._id}`);

                if (response?.success) {
                    const { aiSettings: settings } = response.data;
                    setAiSettings({
                        aiEnabled: settings.aiEnabled || false,
                        aiDescription: settings.aiDescription || '',
                        aiUnderstanding: settings.aiUnderstanding || '',
                        facilityIds: settings.facilityIds || []
                    });
                    setAiUnderstanding(settings.aiUnderstanding || null);
                }
            } catch (error) {
                console.error('Error loading AI settings:', error);
                // Fallback to question data if API fails
                setAiSettings({
                    aiEnabled: question?.aiEnabled || false,
                    aiDescription: question?.aiDescription || '',
                    aiUnderstanding: question?.aiUnderstanding || '',
                    facilityIds: question?.facilityIds || []
                });
            } finally {
                setLoadingSettings(false);
            }

            setError(null);
            setSuccess(false);
            setValidationError(null);
            setSearchText('');
        };

        loadAiSettings();
    }, [open, question, currentAccountId]);

    const handleSave = async () => {
        if (!question?._id) {
            setError('Question ID is required to save AI settings');
            return;
        }

        // Clear previous validation errors
        setValidationError(null);
        setError(null);

        // Allow saving description and understanding without enabling AI
        // Only validate when AI is being enabled
        if (aiSettings.aiEnabled) {
            if (!aiSettings.aiDescription.trim()) {
                setValidationError('AI Field Description is required when AI is enabled');
                return;
            }

            if (aiSettings.facilityIds.length === 0) {
                setValidationError('Please select at least one facility to enable AI');
                return;
            }
        }

        setLoading(true);

        try {
            const response = await api.post(`/api/ai-autofill/ai-settings/${question._id}`, {
                aiEnabled: aiSettings.aiEnabled,
                aiDescription: aiSettings.aiDescription,
                aiUnderstanding: aiUnderstanding || aiSettings.aiUnderstanding,
                facilityIds: aiSettings.facilityIds,                
            });

            if (response?.success) {
                setSuccess(true);
                if (onSave) {
                    onSave(aiSettings);
                }
                setTimeout(() => {
                    onClose();
                }, 1500);
            } else {
                setError(response?.error || 'Failed to save AI settings');
            }
        } catch (err) {
            console.error('Error saving AI settings:', err);
            setError(
                err.response?.data?.error ||
                'Failed to save AI settings. Please try again.'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setAiSettings({
            aiEnabled: false,
            aiDescription: '',
            aiUnderstanding: '',
            facilityIds: []
        });
        setError(null);
        setSuccess(false);
        setValidationError(null);
        setAiUnderstanding(null);
        setSearchText('');
        // Reset new state variables
        setActiveTab(0);
        setChatMessage('');
        setConversationHistory([]);
        setShowReviewButton(false);
        setTestPrompt('');
        setTestPromptResult(null);
        setSelectedMessage(null);
        setTestNotes('');
        setTestPromptTabResult(null);
        onClose();
    };

    const handleFacilityToggle = (facilityId) => {
        setAiSettings(prev => ({
            ...prev,
            facilityIds: prev.facilityIds.includes(facilityId)
                ? prev.facilityIds.filter(id => id !== facilityId)
                : [...prev.facilityIds, facilityId]
        }));
        // Clear validation error when user selects facilities
        if (validationError) {
            setValidationError(null);
        }
    };

    const handleSelectAllFacilities = () => {
        const allFacilityIds = facilities?.map(f => f._id) || [];
        setAiSettings(prev => ({
            ...prev,
            facilityIds: prev.facilityIds.length === allFacilityIds.length ? [] : allFacilityIds
        }));
        // Clear validation error when user selects facilities
        if (validationError) {
            setValidationError(null);
        }
    };

    const getFieldTypeInfo = () => {
        if (!question) return { type: 'Unknown', supportsAI: false };

        const type = question.type || question.customQuestionInputType || 'text';
        // Allow AI for all custom questions and standard question types
        const supportsAI = question.isCustom ||
            ['text', 'validation', 'date', 'select', 'toggle', 'time'].includes(type) ||
            [QUESTION_INPUT_TYPE.UNLIMITED_ANSWERS, QUESTION_INPUT_TYPE.INPUT_TEXT, QUESTION_INPUT_TYPE.SINGLE_SELECT, QUESTION_INPUT_TYPE.MULTI_SELECT, QUESTION_INPUT_TYPE.YES_NO, QUESTION_INPUT_TYPE.DATE_RANGE, QUESTION_INPUT_TYPE.TIME_TAB_RESIDENT_LIST, QUESTION_INPUT_TYPE.TIME_TAB_RANGE].includes(question.customQuestionInputType);

        return { type, supportsAI };
    };

    const { type, supportsAI } = getFieldTypeInfo();

    const handleVerify = async () => {
        if (!aiSettings.aiDescription.trim()) {
            setValidationError('Please enter a description before verifying');
            return;
        }

        setVerifying(true);
        setError(null);
        setValidationError(null);

        try {
            const response = await api.post('/api/ai-autofill/verify-ai-description', {
                aiDescription: aiSettings.aiDescription,
                fieldLabel: question?.order?.label || question?.label,
                fieldType: type
            });

            if (response?.success) {
                const understanding = response.data.aiUnderstanding;
                setAiUnderstanding(understanding);
                // Also save to aiSettings for persistence
                setAiSettings(prev => ({ ...prev, aiUnderstanding: understanding }));
            } else {
                setError(response?.error || 'Failed to verify description');
            }
        } catch (err) {
            console.error('Error verifying AI description:', err);
            setError(
                err.response?.data?.error ||
                'Failed to verify AI description. Please try again.'
            );
        } finally {
            setVerifying(false);
        }
    };

    // New handler functions for enhanced features
    const handleChatWithAI = async () => {
        if (!chatMessage.trim()) return;

        setChatLoading(true);
        setError(null);

        // Auto-scroll to bottom when starting to type
        setTimeout(() => {
            if (chatContainerRef) {
                chatContainerRef.scrollTop = chatContainerRef.scrollHeight;
            }
        }, 100);

        try {
            const response = await aiService.chatWithAI(
                chatMessage,
                aiSettings.aiDescription,
                question?.order?.label || question?.label,
                type,
                conversationHistory
            );

            const newMessage = {
                role: 'user',
                content: chatMessage,
                timestamp: new Date().toISOString()
            };

            const aiResponse = {
                role: 'assistant',
                content: response.response,
                timestamp: new Date().toISOString()
            };

            setConversationHistory(prev => [...prev, newMessage, aiResponse]);
            setChatMessage('');

            // Auto-scroll to bottom after adding messages
            setTimeout(() => {
                if (chatContainerRef) {
                    chatContainerRef.scrollTop = chatContainerRef.scrollHeight;
                }
            }, 100);
        } catch (err) {
            console.error('Error chatting with AI:', err);
            setError(err.message || 'Failed to chat with AI. Please try again.');
        } finally {
            setChatLoading(false);
        }
    };


    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        // Clear any errors when switching tabs
        setError(null);
        setValidationError(null);

        // Auto-populate AI Chat with description when switching to AI Chat tab
        if (newValue === 2 && aiSettings.aiDescription.trim() && conversationHistory.length === 0) {
            const initialMessage = {
                role: 'assistant',
                content: aiSettings.aiDescription,
                timestamp: new Date().toISOString()
            };
            setConversationHistory([initialMessage]);
        }
    };

    // Handle applying AI suggestion to description
    const handleApplySuggestion = (suggestion) => {
        setAiSettings(prev => ({
            ...prev,
            aiDescription: suggestion,
            aiUnderstanding: suggestion
        }));
        setAiUnderstanding(suggestion);
        setError(null);
        setValidationError(null);
        setShowReviewButton(true);

        // Show success message
        // setSuccess(true);
        // setTimeout(() => setSuccess(false), 3000);
    };

    // Handle testing AI message as prompt
    const handleTestAIMessage = async (messageContent) => {

        
        if (!question?._id) {
            setError('Question ID is required for testing');
            return;
        }

        if (!aiSettings.aiDescription.trim()) {
            setError('AI description is required for testing. Please configure AI settings first.');
            return;
        }

        // Find the message object from conversation history
        const messageObject = conversationHistory.find(msg => msg.content === messageContent);
        if (messageObject) {
            setSelectedMessage(messageObject);
        }

        // Switch to AI Test Prompt tab immediately
        setActiveTab(3);
        
        setTestPromptTabLoading(true);
        setError(null);
        setTestPromptTabResult(null);

        try {
            const result = await aiService.testQuestion(
                question._id,
                aiSettings.aiDescription,
                messageContent,
                facilityId,
                currentAccountId,
                facilities?.map(f => f._id) || []
            );

            setTestPromptTabResult(result);
        } catch (err) {
            console.error('Error testing AI message:', err);
            setError(err.message || 'Failed to test AI message. Please try again.');
        } finally {
            setTestPromptTabLoading(false);
        }
    };

    // Handle copying message content
    const handleCopyMessage = async (content, messageIndex) => {
        try {
            await navigator.clipboard.writeText(content);
            setCopiedMessageIndex(messageIndex);
            setTimeout(() => setCopiedMessageIndex(null), 2000);
        } catch (err) {
            console.error('Failed to copy text:', err);
        }
    };

    // Handle review button click
    const handleReviewDescription = () => {
        setActiveTab(1); // Switch to Configuration tab
        setShowReviewButton(false);
    };

    // Handle AI test prompt
    const handleTestPrompt = async () => {
        if (!testPrompt.trim()) {
            setError('Please enter a test prompt');
            return;
        }

        if (!question?._id) {
            setError('Question ID is required for testing');
            return;
        }

        setTestPromptLoading(true);
        setError(null);
        setTestPromptResult(null);

        try {
            const result = await aiService.testQuestion(
                question._id,
                aiSettings.aiDescription,
                testPrompt.trim(),
                facilityId,
                currentAccountId,
                facilities?.map(f => f._id) || []
            );

            setTestPromptResult(result);
        } catch (err) {
            console.error('Error testing prompt:', err);
            setError(err.message || 'Failed to test prompt. Please try again.');
        } finally {
            setTestPromptLoading(false);
        }
    };

    // Handle AI Test Prompt Tab
    const handleTestPromptTab = async () => {
        if (!selectedMessage) {
            setError('Please select a message from the AI Chat');
            return;
        }

        if (!testNotes.trim()) {
            setError('Please add notes for the test');
            return;
        }

        if (!question?._id) {
            setError('Question ID is required for testing');
            return;
        }

        setTestPromptTabLoading(true);
        setError(null);
        setTestPromptTabResult(null);

        try {
            // Combine selected message with notes
            const testData = `${selectedMessage.content}\n\nNotes: ${testNotes.trim()}`;

            const result = await aiService.testQuestion(
                question._id,
                aiSettings.aiDescription,
                testData,
                facilityId,
                currentAccountId,
                facilities?.map(f => f._id) || []
            );

            setTestPromptTabResult(result);
        } catch (err) {
            console.error('Error testing prompt:', err);
            setError(err.message || 'Failed to test prompt. Please try again.');
        } finally {
            setTestPromptTabLoading(false);
        }
    };

    if (!supportsAI) {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="md" width="md" fullWidth>
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 24px',
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <AIIcon color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            AI Configuration
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ padding: '24px' }}>
                    <Alert severity="warning">
                        AI auto-fill is not supported for this field type ({type}).
                        AI works best with text input fields, validation fields, and date fields.
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ padding: '16px 24px' }}>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            scroll="paper"
            disableScrollLock
            PaperProps={{
                sx: {
                    height: '90%',
                    maxHeight: '800px',
                    minHeight: '600px',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '20px 24px',
                borderBottom: '1px solid #e0e0e0',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <AIIcon sx={{ fontSize: '28px', color: 'white' }} />
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, marginBottom: '4px', color: 'white' }}>
                            AI Configuration
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, color: 'white' }}>
                            {question?.order?.label || question?.label}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={handleClose} size="small" sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={{ px: 3 }}
                    variant="fullWidth"
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab
                        label="Settings"
                        icon={<SettingsIcon />}
                        iconPosition="start"
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    />
                    <Tab
                        label="Configuration"
                        icon={<TuneIcon />}
                        iconPosition="start"
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    />
                    <Tab
                        label="AI Chat"
                        icon={<ChatIcon />}
                        iconPosition="start"
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    />
                    <Tab
                        label="AI Test Prompt"
                        icon={<PlayArrowIcon />}
                        iconPosition="start"
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    />
                </Tabs>
            </Box>

            {/* Error and Success Messages */}
            {(error || success || validationError) && (
                <Box sx={{ padding: '16px 24px 0' }}>
                    {error && (
                        <Alert severity="error" sx={{ marginBottom: '16px' }}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert severity="success" sx={{ marginBottom: '16px' }}>
                            AI settings saved successfully!
                        </Alert>
                    )}
                    {validationError && (
                        <Alert severity="warning" sx={{ marginBottom: '16px' }}>
                            {validationError}
                        </Alert>
                    )}
                </Box>
            )}

            <DialogContent sx={{ 
                padding: '24px', 
                overflow: 'auto',
                flex: 1,
                display: 'flex',
                flexDirection: 'column'
            }}>
                {activeTab === 0 && (
                    <SettingsTab
                        aiSettings={aiSettings}
                        setAiSettings={setAiSettings}
                        facilities={facilities}
                        searchText={searchText}
                        setSearchText={setSearchText}
                        handleFacilityToggle={handleFacilityToggle}
                        handleSelectAllFacilities={handleSelectAllFacilities}
                        loading={isFacilityLoading}
                        loadingSettings={loadingSettings}
                    />
                )}
                {activeTab === 1 && (
                    <ConfigurationTab
                        aiSettings={aiSettings}
                        setAiSettings={setAiSettings}
                        validationError={validationError}
                        setValidationError={setValidationError}
                        aiUnderstanding={aiUnderstanding}
                        setAiUnderstanding={setAiUnderstanding}
                        verifying={verifying}
                        handleVerify={handleVerify}
                        testPrompt={testPrompt}
                        setTestPrompt={setTestPrompt}
                        testPromptLoading={testPromptLoading}
                        testPromptResult={testPromptResult}
                        handleTestPrompt={handleTestPrompt}
                        question={question}
                    />
                )}
                {activeTab === 2 && (
                    <AiChatTab
                        aiSettings={aiSettings}
                        chatMessage={chatMessage}
                        setChatMessage={setChatMessage}
                        chatLoading={chatLoading}
                        conversationHistory={conversationHistory}
                        chatContainerRef={chatContainerRef}
                        setChatContainerRef={setChatContainerRef}
                        copiedMessageIndex={copiedMessageIndex}
                        showReviewButton={showReviewButton}
                        handleChatWithAI={handleChatWithAI}
                        handleCopyMessage={handleCopyMessage}
                        handleApplySuggestion={handleApplySuggestion}
                        handleTestAIMessage={handleTestAIMessage}
                        handleReviewDescription={handleReviewDescription}
                        question={question}
                    />
                )}
                {activeTab === 3 && (
                    <AiTestPromptTab
                        selectedMessage={selectedMessage}
                        setSelectedMessage={setSelectedMessage}
                        testNotes={testNotes}
                        setTestNotes={setTestNotes}
                        testPromptTabLoading={testPromptTabLoading}
                        testPromptTabResult={testPromptTabResult}
                        handleTestPromptTab={handleTestPromptTab}
                        conversationHistory={conversationHistory}
                        question={question}
                        aiSettings={aiSettings}
                    />
                )}
            </DialogContent>

            <Divider />
            <DialogActions sx={{ padding: '20px 24px', backgroundColor: '#f8f9fa' }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={loading}
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)'
                        }
                    }}
                >
                    {loading ? 'Saving...' : 'Save Settings'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AiConfigDialog;
